import React, { FC, useRef, useState } from 'react';
import classNames from 'classnames';
import { FormLabel } from './FormLabel';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { FileError } from '../../interfaces';

interface FormFileProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  name: string;
  label?: string;
  selected?: File | File[] | null;
  description?: string;
  containerClassname?: string;
  labelContainerClassname?: string;
  labelClassname?: string;
  error?: FileError[]; // Solo FileError[] ahora
  accept?: string;
  multiple?: boolean;
  accumulate?: boolean;
  onSelectFile: (file?: File | File[] | null) => void;
}

export const FormFileWithLabel: FC<FormFileProps> = ({
  label = '',
  name,
  error = [], // Valor por defecto como array vacío
  disabled,
  selected = null,
  description,
  className,
  containerClassname = 'flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row',
  labelContainerClassname = 'flex sm:!justify-end sm:w-32',
  labelClassname = '!text-xs',
  accept,
  multiple = false,
  onSelectFile,
  accumulate = false, // Valor por defecto false
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const truncateFileName = (fileName: string, maxLength = 60) => {
    return fileName.length > maxLength
      ? `${fileName.substring(0, maxLength)}...`
      : fileName;
  };
  const MAX_FILES = 5;

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);

    if (!e.dataTransfer.files?.length) return;

    const files = Array.from(e.dataTransfer.files);
    const filteredFiles = accept
      ? files.filter((file) =>
          accept.split(',').some((ext) => file.name.endsWith(ext.trim()))
        )
      : files;

    if (filteredFiles.length > 0) {
      if (multiple) {
        const currentFiles = selected
          ? Array.isArray(selected)
            ? selected
            : [selected]
          : [];

        if (currentFiles.length + filteredFiles.length > MAX_FILES) {
          return;
        }

        handleFileSelection([...currentFiles, ...filteredFiles]); // Usamos handleFileSelection
      } else {
        handleFileSelection(filteredFiles[0]); // Usamos handleFileSelection
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const files = Array.from(e.target.files);
    const filteredFiles = accept
      ? files.filter((file) =>
          accept.split(',').some((ext) => file.name.endsWith(ext.trim()))
        )
      : files;

    if (filteredFiles.length > 0) {
      if (multiple && accumulate) {
        const currentFiles = selected
          ? Array.isArray(selected)
            ? selected
            : [selected]
          : [];

        if (currentFiles.length + filteredFiles.length > MAX_FILES) {
          return;
        }

        handleFileSelection([...currentFiles, ...filteredFiles]); // Usamos handleFileSelection
      } else {
        handleFileSelection(multiple ? filteredFiles : filteredFiles[0]); // Usamos handleFileSelection
      }
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const getFileErrors = (file: File) => {
    if (!error || error.length === 0) return null;

    // Compara por nombre y tamaño
    const fileError = error.find(
      (err) => err.fileName === file.name && err.fileSize === file.size
    );

    return fileError?.message || null;
  };

  const handleFileSelection = (files?: File | File[] | null) => {
    if (!files || (Array.isArray(files) && files.length === 0)) {
      onSelectFile(undefined);
    } else {
      onSelectFile(files);
    }
  };

  const renderSelectedFiles = () => {
    if (!selected) return null;

    const files = Array.isArray(selected) ? selected : [selected];
    const hasErrors = error && error.length > 0;

    return (
      <>
        <div className="selected-files-container flex flex-wrap gap-2">
          {files.map((file, index) => {
            const fileError = getFileErrors(file);
            const isInvalid = !!fileError;
            const displayName = truncateFileName(file.name, 30);

            return (
              <div
                key={`${file.name}-${file.size}-${index}`}
                className={classNames(
                  'flex items-center rounded-full px-3 py-1 text-xs border',
                  'max-w-xs transition-all duration-200',
                  isInvalid
                    ? 'bg-red-50 border-red-200 text-red-600'
                    : 'bg-gray-50 border-gray-200'
                )}
                title={fileError || file.name}
              >
                <span className="truncate">{displayName}</span>
                <XMarkIcon
                  className="w-3 h-3 ml-1 cursor-pointer flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (Array.isArray(selected)) {
                      const newFiles = selected.filter((_, i) => i !== index);
                      handleFileSelection(
                        newFiles.length > 0 ? newFiles : undefined
                      ); // Usamos handleFileSelection
                    } else {
                      handleFileSelection(undefined); // Usamos handleFileSelection
                    }
                  }}
                />
              </div>
            );
          })}
        </div>

        {hasErrors && (
          <div className="mt-2 text-xs text-red-600">
            <p>
              Archivos marcados en rojo contienen errores. Coloque el mouse
              sobre cada archivo para ver el detalle.
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={containerClassname}>
      <div className="flex items-start gap-8 w-full">
        <FormLabel
          label={label}
          htmlFor={name}
          className={labelClassname}
          containerClassname={labelContainerClassname}
        />

        <div className="flex-1 flex flex-col">
          <div
            tabIndex={0}
            onClick={() => {
              if (selected && Array.isArray(selected)) {
                if (selected.length >= MAX_FILES) return;
              }
              inputRef.current?.click();
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={classNames(
              'h-8 flex items-center px-4 py-2 cursor-pointer w-full',
              'border border-gray-300 rounded-md shadow-sm',
              isDraggingOver ? 'bg-gray-100' : 'bg-white',
              error && error.length > 0 ? 'border-red-500' : '',
              disabled && 'bg-gray-100 text-gray-500',
              selected &&
                Array.isArray(selected) &&
                selected.length >= MAX_FILES
                ? 'bg-gray-100 cursor-not-allowed'
                : '',
              className
            )}
          >
            <CloudArrowUpIcon className="w-4 h-4 mr-2 text-main-500 flex-shrink-0" />

            {!selected ? (
              <span className="text-gray-400 truncate flex-1 text-xs">
                {description ||
                  (multiple
                    ? 'Selecciona o arrastra archivos'
                    : 'Selecciona o arrastra un archivo')}
              </span>
            ) : (
              <span className="text-gray-600 truncate flex-1 text-xs">
                {Array.isArray(selected)
                  ? `${selected.length} archivo(s) seleccionado(s)`
                  : '1 archivo seleccionado'}
              </span>
            )}
          </div>

          <input
            {...props}
            type="file"
            id={name}
            name={name}
            ref={inputRef}
            disabled={disabled}
            onChange={handleChange}
            className="hidden"
            accept={accept}
            multiple={multiple}
          />

          {renderSelectedFiles()}
        </div>
      </div>
    </div>
  );
};
